import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Logo, rupee, to12h } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Star, Check, ChevronLeft, ChevronRight, MessageCircle, CalendarIcon, User, Clock, Search, X, Scissors, Sparkles, Tag, Package, Plus, MapPin } from "lucide-react";
import { toast } from "sonner";
import { istNow, ymd } from "../lib/dates";
import HomeFeed, { BookHeader, BottomBars, ServiceCard } from "../components/booking/HomeFeed";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace("://billing.definesalon.com", "://billing.definesalons.com");
const photoUrl = (p) => (p ? (p.startsWith("http") ? p : `${BACKEND}${p}`) : "");
// Curated hero imagery for the primary category cards (admin can override via category images)
const HERO = {
  "women-services": "https://images.pexels.com/photos/3993320/pexels-photo-3993320.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "men-services": "https://images.pexels.com/photos/32329615/pexels-photo-32329615.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "women-packages": "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  "men-packages": "https://images.pexels.com/photos/7781844/pexels-photo-7781844.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "offers": "https://images.pexels.com/photos/5632347/pexels-photo-5632347.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "category": "https://images.unsplash.com/photo-1582095133179-bfd08e2fc6b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
};
const today = () => ymd(istNow());
const prettyDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "";

const BranchCard = ({ b, on, testId, onPick }) => (
  <Card onClick={onPick} data-testid={testId} aria-pressed={on}
    className={`p-5 border-2 cursor-pointer transition-colors duration-150 ${on ? "bg-primary/10 border-primary ring-2 ring-primary/30 shadow-md" : "bg-card border-border hover:border-primary/60"}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-display text-xl flex items-center gap-2 flex-wrap">{b.name}{on && <span className="text-[10px] uppercase tracking-wide rounded-full bg-primary text-white px-2 py-0.5" data-testid={`book-branch-selected-${b.id}`}>Selected</span>}</p>
        {b.address && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line" data-testid={`book-branch-address-${b.id}`}>{b.address}</p>}
      </div>
      {b.maps_link && <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); window.open(b.maps_link, "_blank", "noopener"); }} data-testid={`book-branch-map-${b.id}`} className="shrink-0 rounded-full gap-1.5 border-primary/40 text-primary hover:bg-primary/10"><MapPin size={14} /> View Location</Button>}
    </div>
  </Card>
);
const addMin = (hhmm, m) => { const [h, mm] = (hhmm || "0:0").split(":").map(Number); const t = h * 60 + mm + (Number(m) || 0); return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };
const fmtDur = (m) => { const h = Math.floor(m / 60), mm = m % 60; return [h ? `${h} hour${h > 1 ? "s" : ""}` : "", mm ? `${mm} minutes` : ""].filter(Boolean).join(" ") || "0 minutes"; };

const LABELS = { cart: "Cart", details: "Your Details", branch: "Branch", method: "Method", staff: "Staff", date: "Date", time: "Time", summary: "Summary", fpservices: "Services", fpstaff: "Staff" };
const BASE = ["details", "branch", "method"];
const STAFF_STEPS = ["staff", "date", "time", "summary"];
const TIME_STEPS = ["date", "time", "staff", "summary"];

export default function PublicBooking() {
  const [active, setActive] = useState(false); // wizard active?
  const [step, setStep] = useState(0);
  const [method, setMethod] = useState(null); // "staff" | "time"
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [offer, setOffer] = useState(null);
  const [categories, setCategories] = useState([]);
  const [slots, setSlots] = useState([]);
  const [offMsg, setOffMsg] = useState("");
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffPick, setStaffPick] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [cart, setCart] = useState([]);
  const [sel, setSel] = useState({ branch: null, date: null, time: null });
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [cust, setCust] = useState({ id: null, name: "", mobile: "", gender: "" });
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    const mob = (cust.mobile || "").replace(/\D/g, "");
    if (mob.length !== 10) { setPrefilled(false); return; }
    let live = true;
    api.get("/appointments/public/customer-lookup", { params: { mobile: mob } }).then(({ data }) => {
      if (!live || !data.found) return;
      setCust((c) => ({ ...c, id: data.id, name: c.name.trim() ? c.name : data.name, gender: c.gender || data.gender }));
      setPrefilled(true);
    }).catch(() => {});
    return () => { live = false; };
  }, [cust.mobile]);
  const [done, setDone] = useState(false);
  const [svcQuery, setSvcQuery] = useState("");
  const [activeCat, setActiveCat] = useState("ALL");
  const [fpCfg, setFpCfg] = useState({ enabled: false, slabs: [], services: [] });
  const [fpMode, setFpMode] = useState(null);        // chosen slab object
  const [fpStaff, setFpStaff] = useState({});        // {serviceId: staffId}
  const [fpEligible, setFpEligible] = useState({});  // {serviceId: [staff]}
  const [pkgs, setPkgs] = useState([]);              // combo/offer packages
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [view, setView] = useState({ type: "home" });  // home | cats | svclist | packages
  const [homeGender, setHomeGender] = useState("women");
  const [branchTick, setBranchTick] = useState(0);
  const [branchPick, setBranchPick] = useState(false);
  const [homeCards, setHomeCards] = useState([]);     // admin-managed primary cards
  const [banners, setBanners] = useState([]);          // admin-managed promo banners

  const RED = "#B71C2B";
  const forGender = (s, g) => g === "all" || s.gender === "unisex" || !s.gender || s.gender === (g === "women" ? "female" : "male");
  const catsForGender = (g) => {
    const map = new Map();
    services.filter((s) => forGender(s, g)).forEach((s) => { const c = s.category || "Other"; map.set(c, (map.get(c) || 0) + 1); });
    const ordered = categories.map((c) => c.name).filter((n) => map.has(n));
    const extra = [...map.keys()].filter((n) => !ordered.includes(n));
    return [...ordered, ...extra].map((name) => ({ name, count: map.get(name), image: catImage(name) }));
  };
  const listServices = () => {
    let list = services;
    if (view.type === "svclist") {
      if (view.gender) list = list.filter((s) => forGender(s, view.gender));
      if (view.category) list = list.filter((s) => (s.category || "Other") === view.category);
      if (view.offersOnly) list = list.filter((s) => (s.actual_price || 0) > (s.price || 0));
    }
    if (svcQuery.trim()) list = list.filter(svcMatch);
    return list;
  };
  const goHome = () => { setView({ type: "home" }); setSvcQuery(""); };
  const cardGo = (c) => {
    const g = c.gender === "men" ? "men" : c.gender === "women" ? "women" : "all";
    if (c.dest_type === "custom" && c.custom_url) { window.open(c.custom_url, "_blank", "noopener"); return; }
    if (c.dest_type === "packages") return setView({ type: "packages", gender: g === "all" ? "women" : g, title: c.title });
    if (c.dest_type === "offers") return setView({ type: "svclist", gender: "all", category: null, offersOnly: true, title: c.title });
    if (c.dest_type === "category") return setView({ type: "svclist", gender: g, category: c.category || null, title: c.title });
    return setView({ type: "cats", gender: g === "all" ? "women" : g, title: c.title });
  };
  const heroFor = (c) => {
    if (c.image) return photoUrl(c.image, 480);
    const k = c.dest_type === "offers" ? "offers"
      : c.dest_type === "packages" ? (c.gender === "men" ? "men-packages" : "women-packages")
      : c.dest_type === "category" ? "category"
      : (c.gender === "men" ? "men-services" : "women-services");
    return HERO[k] || HERO.category;
  };
  const heroBanner = banners.length
    ? { admin: true, image: banners[0].image, title: banners[0].heading, description: banners[0].offer_text,
        old_price_text: banners[0].old_price_text, new_price_text: banners[0].new_price_text }
    : (offer && (offer.title || offer.image) ? { admin: false, ...offer } : null);
  const _slug = (t) => (t || "x").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const genderImg = (g) => { const hc = homeCards.find((c) => c.gender === g && c.image); return hc ? photoUrl(hc.image, 480) : HERO[g === "men" ? "men-services" : "women-services"]; };
  const pkgsForGender = (g) => pkgs.filter((p) => { const pg = p.gender || "women"; return g === "men" ? (pg === "men" || pg === "unisex") : (pg === "women" || pg === "unisex" || !p.gender); });
  const landingCards = (g) => {
    const out = [];
    const freedomHere = g === "women" && fpCfg.enabled && (fpCfg.slabs || []).length > 0;
    const pkgCount = pkgsForGender(g).length + (freedomHere ? 1 : 0);
    const pkgHc = homeCards.find((c) => c.dest_type === "packages" && c.gender === g && c.image);
    if (pkgCount > 0) out.push({ key: "packages", slug: "packages", name: g === "women" ? "Women Packages" : "Men Packages", image: pkgHc ? photoUrl(pkgHc.image) : HERO[g === "men" ? "men-packages" : "women-packages"], count: pkgCount, go: () => setView({ type: "packages", gender: g, title: g === "women" ? "Women Packages" : "Men Packages" }) });
    catsForGender(g).forEach((c) => out.push({ key: c.name, slug: _slug(c.name), name: c.name, image: c.image ? photoUrl(c.image) : "", count: c.count, go: () => setView({ type: "svclist", gender: g, category: c.name, title: c.name }) }));
    out.push({ key: "offers", slug: "offers", name: "Special Offers", badge: "OFFERS", image: HERO.offers, count: null, go: () => setView({ type: "svclist", gender: g, category: null, offersOnly: true, title: "Special Offers" }) });
    return out;
  };


  const goBack = () => {
    if ((view.type === "svclist" || view.type === "packages") && view.gender && view.gender !== "all") setView({ type: "cats", gender: view.gender, title: (view.gender === "men" ? "MEN" : "WOMEN") });
    else setView({ type: "home" });
    setSvcQuery("");
  };
  const renderSubHeader = (title) => (
    <div className="flex items-center gap-2 mb-5" data-testid="view-subheader">
      <button type="button" onClick={goBack} data-testid="view-back"
        className="h-9 w-9 grid place-items-center rounded-full border border-border bg-white hover:border-primary text-foreground"><ChevronLeft size={18} /></button>
      <h1 className="font-display text-2xl sm:text-3xl">{title}</h1>
    </div>
  );



  const catImage = (name) => (categories.find((c) => c.name === name) || {}).image;
  const svcMatch = (s) => {
    const t = svcQuery.trim().toLowerCase();
    if (!t) return true;
    return (s.name || "").toLowerCase().includes(t) || (s.category || "").toLowerCase().includes(t);
  };
  const homeCats = (() => {
    const svcCats = new Set(services.map((s) => s.category || "Other"));
    const ordered = categories.map((c) => c.name).filter((n) => svcCats.has(n));
    const extra = [...svcCats].filter((n) => !categories.some((c) => c.name === n));
    return ["ALL", ...ordered, ...extra];
  })();
  const homeServices = services.filter((s) => (activeCat === "ALL" || (s.category || "Other") === activeCat) && svcMatch(s));

  const flow = method === "freedom"
    ? ["details", "branch", "fpservices", "date", "fpstaff", "time", "summary"]
    : method === "multi"
    ? ["cart", "date", "fpstaff", "time", "details", "summary"]
    : method === "combopkg"
      ? ["details", "branch", "staff", "date", "time", "summary"]
      : [...BASE, ...(method === "staff" ? STAFF_STEPS : method === "time" ? TIME_STEPS : [])];
  const stepKey = flow[step];
  const totalDuration = cart.reduce((s, x) => s + (x.duration || 30) * Math.max(1, x.qty || 1), 0);
  const totalPrice = cart.reduce((a, x) => a + (x.price || 0) * Math.max(1, x.qty || 1), 0);

  useEffect(() => {
    // Catalogue calls: paint instantly from the last session cache, then refresh in the background (stale-while-revalidate)
    const cached = (url, setter, fallback, params) => {
      const key = `book:${url}${params ? JSON.stringify(params) : ""}`;
      try { const hit = JSON.parse(sessionStorage.getItem(key) || "null"); if (hit && Date.now() - hit.t < 10 * 60 * 1000) setter(hit.d); } catch {}
      return api.get(url, params ? { params } : undefined).then((r) => { const d = r.data ?? fallback; setter(d); try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d })); } catch {} })
        .catch(() => setter((cur) => (cur && (Array.isArray(cur) ? cur.length : true) ? cur : fallback)));
    };
    Promise.allSettled([
      cached("/appointments/public/services", setServices, []),
      cached("/public/service-categories", setCategories, []),
      cached("/public/home-cards", setHomeCards, []),
      cached("/public/promo-banners", setBanners, []),
    ]).then(() => setCatalogLoading(false));
    // secondary data (not needed for the first screen)
    cached("/appointments/public/branches", setBranches, []);
    cached("/public/offers", setOffer, null);
    cached("/public/freedom-package", setFpCfg, { enabled: false, slabs: [], services: [] });
    cached("/public/combo-packages", setPkgs, []);
  }, []);

  useEffect(() => {
    if (sel.branch?.id) {
      api.get("/public/freedom-package", { params: { branch_id: sel.branch.id } }).then((r) => setFpCfg(r.data)).catch(() => {});
      api.get("/public/combo-packages", { params: { branch_id: sel.branch.id } }).then((r) => setPkgs(r.data || [])).catch(() => {});
    }
  }, [sel.branch]);

  const [pendingWizard, setPendingWizard] = useState(false);
  // Multi-person booking: each service is "for" a person; people run in parallel, one person's services run back-to-back
  const [persons, setPersons] = useState([{ n: 1, gender: "" }]);
  const svcGender = (svc) => (svc.gender === "male" ? "male" : svc.gender === "female" ? "female" : "");
  const personOf = (svc) => svc.person || 1;
  const personGender = (n, svc) => (persons.find((p) => p.n === n)?.gender) || (svc ? svcGender(svc) : "") || "";
  const setPersonGender = (n, g) => setPersons((ps) => ps.map((p) => (p.n === n ? { ...p, gender: g } : p)));
  const addPerson = () => { const n = Math.max(...persons.map((p) => p.n)) + 1; setPersons((ps) => [...ps, { n, gender: "" }]); return n; };
  const [unitPersons, setUnitPersons] = useState({});   // {"svcId#n": personNo} — every unit can belong to a different person
  const unitPerson = (svc, n) => unitPersons[`${svc.id}#${n}`] || svc.person || 1;
  const setUnitPerson = (svc, n, p) => { setUnitPersons((m) => ({ ...m, [`${svc.id}#${n}`]: p })); setSel((x) => ({ ...x, time: null })); };
  const needBranch = () => { if (!sel.branch && view.type !== "pickbranch") { setView({ type: "pickbranch", back: view }); return true; } return false; };
  const toggleService = (s) => { const adding = !cart.find((x) => x.id === s.id); setCart((c) => c.find((x) => x.id === s.id) ? c.filter((x) => x.id !== s.id) : [...c, { ...s, qty: 1 }]); if (adding) needBranch(); };
  const pickBranch = (b) => { setSel((x) => ({ ...x, branch: b, time: null })); if (window.history.state?.bk?.view?.type === "pickbranch") window.history.back(); else setView(view.back || { type: "home" }); };
  useEffect(() => { if (pendingWizard && sel.branch && view.type !== "pickbranch") { setPendingWizard(false); startWizard(); } }, [pendingWizard, sel.branch, view.type]); // eslint-disable-line react-hooks/exhaustive-deps
  const setQty = (s, qty) => setCart((c) => qty <= 0 ? c.filter((x) => x.id !== s.id) : c.map((x) => (x.id === s.id ? { ...x, qty: Math.min(4, qty) } : x)));
  // one booking item per service unit: Haircut #1, Haircut #2, Hair Spa #1 …
  // First service unit is always "For Me" (no prompt); only later units ask who they are for
  const items = cart.flatMap((svc) => Array.from({ length: Math.max(1, svc.qty || 1) }, (_, i) => ({ key: `${svc.id}#${i + 1}`, svc, n: i + 1, person: unitPerson(svc, i + 1) }))).map((it, i) => (i === 0 ? { ...it, person: 1 } : it));
  const isFirstUnit = (svc, n) => items[0]?.key === `${svc.id}#${n}`;
  const personsCsv = () => items.map((it) => it.person).join(",");
  const spanDuration = (() => { const m = {}; items.forEach((it) => { m[it.person] = (m[it.person] || 0) + (it.svc.duration || 30); }); return Math.max(0, ...Object.values(m)); })();
  const finishTime = sel.time ? addMin(sel.time, method === "multi" ? spanDuration : totalDuration) : null;
  const svcIdsCsv = () => items.map((it) => it.svc.id).join(",");
  const itemStaff = (it) => (method === "freedom" ? fpStaff[it.svc.id] : fpStaff[it.key]) || "";

  const loadStaffEligible = async (branchId) => {
    setLoading(true); setStaffOptions([]); setStaffPick(null);
    try {
      const { data } = await api.get("/appointments/public/combo-staff-eligible", {
        params: { branch_id: branchId || sel.branch.id, service_ids: svcIdsCsv() },
      });
      setStaffOptions(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadStaffSlots = async () => {
    setLoading(true); setSlots([]); setOffMsg("");
    try {
      const st = await api.get("/appointments/public/staff-day-status", { params: { staff_id: staffPick.id, date: sel.date } });
      if (st.data?.off) { setOffMsg(st.data.message); setLoading(false); return; }
      const { data } = await api.get("/appointments/public/slots", {
        params: { branch_id: sel.branch.id, date: sel.date, duration: totalDuration, service_ids: svcIdsCsv(), staff_id: staffPick.id },
      });
      setSlots(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setSlots([]); }
    finally { setLoading(false); }
  };

  const loadComboSlots = async () => {
    setLoading(true); setSlots([]); setOffMsg("");
    try {
      const { data } = await api.get("/appointments/public/combo-slots", {
        params: { branch_id: sel.branch.id, date: sel.date, duration: totalDuration, service_ids: svcIdsCsv() },
      });
      setSlots(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setSlots([]); }
    finally { setLoading(false); }
  };

  const loadComboStaff = async () => {
    setLoading(true); setStaffOptions([]); setStaffPick(null);
    try {
      const { data } = await api.get("/appointments/public/combo-staff", {
        params: { branch_id: sel.branch.id, date: sel.date, start_time: sel.time, service_ids: svcIdsCsv() },
      });
      setStaffOptions(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const blockTimes = () => { const cur = {}; return items.map((it) => { const start = cur[it.person] || sel.time; const end = addMin(start, it.svc.duration || 30); cur[it.person] = end; return { start, end, it }; }); };

  const loadFreedomSlots = async () => {
    setLoading(true); setSlots([]);
    try {
      const staffIds = items.map((it) => itemStaff(it)).join(",");
      const { data } = await api.get("/appointments/public/slots", {
        params: { branch_id: sel.branch.id, date: sel.date, duration: totalDuration, service_ids: svcIdsCsv(), staff_ids: staffIds, persons: personsCsv() },
      });
      setSlots(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setSlots([]); }
    finally { setLoading(false); }
  };

  const loadFpEligible = async () => {
    setLoading(true);
    const map = {};
    await Promise.all(cart.map(async (s) => {
      try { const { data } = await api.get("/appointments/public/eligible-staff", { params: { branch_id: sel.branch.id, service_id: s.id, date: sel.date, gender: (() => { const gs = [...new Set(Array.from({ length: s.qty || 1 }, (_, i) => personGender(unitPerson(s, i + 1), s)))]; return gs.length === 1 ? (gs[0] || undefined) : undefined; })() } }); map[s.id] = data; }
      catch { map[s.id] = []; }
    }));
    setFpEligible(map);
    if (method === "multi") preferCommonStaff(map);
    setLoading(false);
  };
  // Same person = same stylist when one eligible stylist can do ALL of that person's services
  const personUnits = (p) => items.filter((it) => it.person === p);
  const commonStaffFor = (p, map = fpEligible) => {
    const us = personUnits(p); if (!us.length) return [];
    return (map[us[0].svc.id] || []).filter((st) => us.every((u) => (map[u.svc.id] || []).some((x) => x.id === st.id)));
  };
  const assignPersonStaff = (p, staffId) => setFpStaff((m) => { const n = { ...m }; personUnits(p).forEach((u) => { n[u.key] = staffId; }); return n; });
  const preferCommonStaff = (map) => {
    const people = [...new Set(items.map((it) => it.person))];
    setFpStaff((m) => {
      const n = { ...m };
      const used = new Set();
      people.forEach((p) => {
        const us = personUnits(p);
        if (us.every((u) => n[u.key])) return;                      // customer already chose
        const common = commonStaffFor(p, map).filter((st) => !used.has(st.id));
        const pick = common[0] || commonStaffFor(p, map)[0];
        if (pick) { us.forEach((u) => { n[u.key] = pick.id; }); used.add(pick.id); }
      });
      return n;
    });
  };

  const fpCounts = (fpCfg.slabs || []).map((s) => s.count);
  const fpMin = fpCounts.length ? Math.min(...fpCounts) : 0;
  const fpMax = fpCounts.length ? Math.max(...fpCounts) : 0;
  const fpSlabFor = (n) => (fpCfg.slabs || []).find((s) => s.count === n) || null;
  const toggleFpService = (s) => setCart((c) => c.find((x) => x.id === s.id) ? c.filter((x) => x.id !== s.id) : (c.length >= fpMax ? c : [...c, s]));
  // Freedom price auto-derives from how many services are selected (2/3/4 → slab price).
  useEffect(() => {
    if (method !== "freedom") return;
    setFpMode(fpSlabFor(cart.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, method, fpCfg.slabs]);
  const startFreedom = () => { setActive(true); setStep(0); setMethod("freedom"); setFpMode(null); setCart([]); setFpStaff({}); setFpEligible({}); setStaffPick(null); setSel((s) => ({ ...s, time: null })); };
  const startPackage = (pkg) => {
    setSelectedPkg(pkg);
    setCart((pkg.services || []).map((s) => ({ id: s.id, name: s.name, duration: s.duration || 30, price: s.price || 0, actual_price: s.price || 0 })));
    setActive(true); setStep(0); setMethod("combopkg"); setStaffPick(null); setSel((s) => ({ ...s, time: null }));
  };

  const book = async () => {
    if (booking) return;
    setBooking(true);
    try {
      let svcs;
      if (method === "freedom") {
        const group = `FP-${Date.now()}`;
        svcs = cart.map((svc) => {
          const stf = (fpEligible[svc.id] || []).find((x) => x.id === fpStaff[svc.id]);
          return { service_id: svc.id, service_name: svc.name, staff_id: fpStaff[svc.id], staff_name: stf?.name || "", duration: svc.duration || 30,
            freedom_group: group, freedom_slab_count: fpMode.count, freedom_slab_price: fpMode.price };
        });
      } else if (method === "multi") {
        svcs = items.map((it) => {
          const stf = (fpEligible[it.svc.id] || []).find((x) => x.id === itemStaff(it));
          return { service_id: it.svc.id, service_name: it.svc.name, staff_id: itemStaff(it), staff_name: stf?.name || "", duration: it.svc.duration || 30, person: it.person, person_gender: personGender(it.person, it.svc) };
        });
      } else {
        svcs = items.map((it) => ({ service_id: it.svc.id, service_name: it.svc.name, staff_id: staffPick.id, staff_name: staffPick.name, duration: it.svc.duration || 30 }));
      }
      await api.post("/appointments/public/multi", {
        customer_id: cust.id || null, customer_name: cust.name, customer_mobile: cust.mobile,
        customer_gender: cust.gender || "", branch_id: sel.branch.id,
        date: sel.date, start_time: sel.time, services: svcs,
        selection_mode: (method === "staff" || method === "combopkg" || method === "multi") ? "staff_first" : "service_first",
        booking_flow: method === "freedom" ? "FREEDOM" : ((method === "staff" || method === "combopkg") ? "STAFF_FIRST" : "TIME_FIRST"), staff_selected_by_customer: true,
      });
      setDone(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBooking(false); }
  };

  const startWizard = () => { if (!sel.branch) { setPendingWizard(true); needBranch(); return; } setActive(true); setStep(0); setMethod("multi"); setStaffPick(null); setSel((s) => ({ ...s, time: null })); };
  const adjustQty = (svc, q) => {
    const old = svc.qty || 1;
    if (q === old) return;
    if (q < old) setFpStaff((m) => { const n = { ...m }; for (let i = q + 1; i <= old; i++) delete n[`${svc.id}#${i}`]; return n; });
    setQty(svc, q);
    setSel((x) => ({ ...x, time: null }));   // later steps (staff for new units, time) are re-validated when reached
  };
  // Summary: change one unit's stylist in place, then re-check the chosen time with the new staff line-up
  const [unitEdit, setUnitEdit] = useState(null);
  const [recheck, setRecheck] = useState(0);
  const changeUnitStaff = (it, staffId) => { const k = method === "freedom" ? it.svc.id : it.key; if (fpStaff[k] === staffId) { setUnitEdit(null); return; } setFpStaff((m) => ({ ...m, [k]: staffId })); setUnitEdit(null); setRecheck((n) => n + 1); };
  useEffect(() => {
    if (!recheck || !sel.time) return;
    (async () => {
      try {
        const { data } = await api.get("/appointments/public/slots", { params: { branch_id: sel.branch.id, date: sel.date, duration: totalDuration, service_ids: svcIdsCsv(), staff_ids: items.map((it) => itemStaff(it)).join(","), persons: personsCsv() } });
        if (data.find((x) => x.time === sel.time && x.available)) { toast.success("Stylist updated — your time is still available"); }
        else { toast.error("That stylist isn't free at this time — please pick a new slot"); setSlots(data); setSel((x) => ({ ...x, time: null })); setStep(flow.indexOf("time")); }
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recheck]);
  const removeSvc = (svc) => { setFpStaff((m) => { const n = { ...m }; Object.keys(n).forEach((k) => { if (k.startsWith(`${svc.id}#`)) delete n[k]; }); return n; }); setQty(svc, 0); setSel((x) => ({ ...x, time: null })); };
  useEffect(() => { if (active && method === "multi" && !cart.length) setActive(false); }, [cart.length, active, method]);
  const changeBranch = (b) => {
    if (b.id === sel.branch?.id) { setBranchPick(false); return; }
    setSel((x) => ({ ...x, branch: b, time: null })); setStaffPick(null); setSlots([]); setStaffOptions([]); setBranchPick(false);
    setBranchTick((t) => t + 1);
  };
  useEffect(() => { // branch changed mid-flow: refresh staff / slots for the new branch
    if (!branchTick || !active || !sel.branch) return;
    if (method === "freedom" || method === "multi") { const i = flow.indexOf("fpstaff"); if (step > i) setStep(i); if (step >= i) loadFpEligible(); return; }
    if (method === "staff" || method === "combopkg") { loadStaffEligible(sel.branch.id); const i = flow.indexOf("staff"); if (step > i) setStep(i); return; }
    if (method === "time") { const i = flow.indexOf("time"); if (step > i) setStep(i); if (step >= i) loadComboSlots(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchTick]);
  const BranchBar = () => sel.branch && !["details", "branch", "method"].includes(stepKey) ? (
    <div className="mb-4 rounded-xl border border-border bg-white shadow-sm" data-testid="book-branch-bar">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="text-primary">📍</span>
        <span className="flex-1 min-w-0 truncate text-[15px] font-semibold" data-testid="book-branch-current">{sel.branch.name.replace(/^Define Salon\s*\d*\s*[–-]\s*/i, "")}</span>
        <button type="button" onClick={() => setBranchPick((v) => !v)} className="text-primary font-semibold text-[14px]" data-testid="book-branch-change">{branchPick ? "Close" : "Change"}</button>
      </div>
      {branchPick && (
        <div className="border-t border-border p-2 space-y-1" data-testid="book-branch-options">
          {branches.map((b) => (
            <button key={b.id} type="button" onClick={() => changeBranch(b)} data-testid={`book-branch-option-${b.id}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-[14px] flex items-center gap-2 ${b.id === sel.branch.id ? "bg-primary/10 text-primary font-semibold" : "hover:bg-secondary"}`}>
              <span className={`h-4 w-4 rounded-full border ${b.id === sel.branch.id ? "border-primary bg-primary" : "border-border"}`} />{b.name.replace(/^Define Salon\s*\d*\s*[–-]\s*/i, "")}
            </button>
          ))}
        </div>
      )}
    </div>
  ) : null;

  const back = () => { window.history.back(); };
  // Real browser-history steps: every wizard step / browse view is its own entry, so Android/browser
  // Back moves exactly one step back and never leaves the site from an internal step. Selections stay in React state.
  const histRestoring = useRef(false);
  const histKey = JSON.stringify({ active, step, view });
  useEffect(() => {
    if (histRestoring.current) { histRestoring.current = false; return; }
    const cur = window.history.state?.bk;
    if (cur && JSON.stringify(cur) === histKey) return;
    if (cur === undefined) window.history.replaceState({ bk: JSON.parse(histKey) }, "");
    else window.history.pushState({ bk: JSON.parse(histKey) }, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [histKey]);
  useEffect(() => {
    const onPop = (e) => {
      const bk = e.state?.bk;
      if (!bk) return;               // left the booking entries -> browser handles normally
      histRestoring.current = true;
      setActive(bk.active); setStep(bk.step); setView(bk.view); setBranchPick(false);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return (
    <div className="define-book min-h-screen bg-background text-foreground">
      {!active && !done ? (
        <BookHeader branches={branches} svcQuery={svcQuery} setSvcQuery={setSvcQuery} hint={services[Math.floor(services.length / 2)]?.name || "Hair Spa"}
          onHome={goHome} onPackages={() => setView({ type: "packages", gender: homeGender, title: "Packages" })} showSearch={view.type === "home" || view.type === "cats" || view.type === "svclist"} />
      ) : (
        <header className="h-16 border-b border-border flex items-center px-5 sm:px-6 bg-white/90 backdrop-blur-xl sticky top-0 z-20">
          <button onClick={goHome} className="flex items-center" data-testid="nav-home"><Logo className="text-xl" /></button>
          <span className="ml-auto text-xs text-muted-foreground hidden sm:inline">Online Booking</span>
        </header>
      )}

      <div className={`max-w-6xl mx-auto ${active || done ? "p-6" : "px-4 pt-1"}`}>
        {done ? (
          <Card className="p-10 bg-card border-border text-center mt-10 max-w-lg mx-auto" data-testid="booking-success">
            <div className="h-16 w-16 rounded-full bg-primary/20 text-primary flex items-center justify-center mx-auto mb-4"><Check size={32} /></div>
            <h1 className="font-display text-3xl">Appointment Booked Successfully</h1>
            {method === "freedom" && fpMode && (
              <div className="mt-5 rounded-xl border border-green-500/40 bg-green-500/10 px-5 py-4" data-testid="fp-success-savings">
                <p className="font-display text-2xl text-green-300">You saved {rupee(cart.reduce((a, x) => a + (Number(x.actual_price) || 0), 0) - fpMode.price)} 🎉</p>
                <p className="text-xs text-muted-foreground mt-1">Freedom Package · Any {fpMode.count} — actual {rupee(cart.reduce((a, x) => a + (Number(x.actual_price) || 0), 0))}, you pay {rupee(fpMode.price)} at the salon</p>
              </div>
            )}
            <div className="mt-5 text-left text-sm space-y-2 bg-secondary rounded-lg p-5" data-testid="booking-success-details">
              <Row label="Branch" value={sel.branch?.name} />
              <div>
                <p className="text-muted-foreground text-xs mb-1">Service(s)</p>
                {blockTimes().map((bt, i) => (
                  <p key={i} className="text-primary" data-testid={`success-svc-${i}`}>{to12h(bt.start)}–{to12h(bt.end)} · {cart[i]?.name}{method === "freedom" ? ` · ${(fpEligible[cart[i]?.id] || []).find((x) => x.id === fpStaff[cart[i]?.id])?.name || ""}` : ""}</p>
                ))}
              </div>
              {method !== "freedom" && <Row label="Stylist" value={staffPick?.name} />}
              <Row label="Date" value={prettyDate(sel.date)} />
              <Row label="Start Time" value={to12h(sel.time)} />
              <Row label="Expected Finish" value={to12h(finishTime)} />
              <Row label="Total Duration" value={fmtDur(totalDuration)} />
              <Row label="Customer" value={cust.name} />
              <Row label="Mobile" value={cust.mobile} />
            </div>
            <p className="text-xs text-muted-foreground mt-4">A WhatsApp confirmation will be sent to {cust.mobile}.</p>
          </Card>
        ) : !active ? (
          <div className="mt-2 pb-24" data-testid="service-menu-home">
            {/* Search (always visible on home) */}
            {view.type === "pickbranch" && (
              <div className="grid gap-3 py-4" data-testid="pick-branch">
                <h1 className="font-display text-3xl mb-1">Select your branch</h1>
                <p className="text-sm text-muted-foreground -mt-1 mb-2">Prices, stylists and timings are branch-specific. You can change it later.</p>
                {branches.map((b) => <BranchCard key={b.id} b={b} on={sel.branch?.id === b.id} testId={`pick-branch-${b.id}`} onPick={() => pickBranch(b)} />)}
              </div>
            )}
            {view.type === "home" && (
              <HomeFeed loading={catalogLoading} services={services} categories={categories} banners={banners} offer={offer} homeCards={homeCards} heroFor={heroFor} cardGo={cardGo}
                pkgs={pkgs} fpCfg={fpCfg} cart={cart} toggleService={toggleService} setQty={setQty} startPackage={startPackage} startFreedom={startFreedom}
                setView={(v) => { setView(v); setSvcQuery(""); }} svcQuery={svcQuery} setSvcQuery={setSvcQuery} gender={homeGender} setGender={setHomeGender} />
            )}

            {/* Gender landing — rich category grid */}
            {view.type === "cats" && (
              <div data-testid="view-cats">
                {renderSubHeader(view.title)}
                {svcQuery.trim() ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cats-search-results">
                    {services.filter((s) => forGender(s, view.gender) && svcMatch(s)).map((s) => <ServiceCard key={s.id} s={s} inCart={!!cart.find((x) => x.id === s.id)} onToggle={toggleService} qty={cart.find((x) => x.id === s.id)?.qty || 0} onQty={setQty} />)}
                    {!services.filter((s) => forGender(s, view.gender) && svcMatch(s)).length && <p className="text-sm text-muted-foreground py-10 text-center">No services match “{svcQuery}”.</p>}
                  </div>
                ) : (
                  <>
                    {heroBanner && (
                      <div className="relative overflow-hidden rounded-2xl border border-border mb-6 min-h-[150px] sm:min-h-[200px] shadow-sm" data-testid="home-offer-card">
                        {heroBanner.image && <img src={photoUrl(heroBanner.image, 1200)} alt={heroBanner.title || "Offer"} className="absolute inset-0 h-full w-full object-cover" />}
                        <div className={`relative h-full p-6 sm:p-9 flex flex-col justify-center ${heroBanner.image ? "bg-gradient-to-r from-black/70 via-black/35 to-transparent" : "bg-gradient-to-br from-primary to-[#7d1420]"}`}>
                          <p className="text-[11px] uppercase tracking-[0.25em] text-white/90 mb-2 flex items-center gap-1"><Tag size={12} />Special Offer</p>
                          <h2 className="font-display text-2xl sm:text-3xl text-white max-w-xl leading-tight">{heroBanner.title}</h2>
                          {heroBanner.description && <p className="text-sm text-white/85 mt-1 max-w-lg">{heroBanner.description}</p>}
                          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                            {heroBanner.old_price_text && <span className="text-white/70 line-through">{heroBanner.old_price_text}</span>}
                            {(heroBanner.new_price_text || heroBanner.price_text) && <span className="text-white text-xl font-semibold">{heroBanner.new_price_text || heroBanner.price_text}</span>}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="cat-grid">
                      {landingCards(view.gender).map((c) => (
                        <button key={c.key} type="button" onClick={c.go} data-testid={`cat-card-${c.slug}`}
                          className="group text-left">
                          <div className="aspect-square w-full rounded-2xl overflow-hidden bg-secondary border border-border shadow-sm relative">
                            {c.image ? <img src={c.image} alt={c.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="h-full w-full grid place-items-center text-primary"><Scissors size={26} /></div>}
                            {c.badge && <span className="absolute top-2 left-2 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5">{c.badge}</span>}
                          </div>
                          <p className="mt-2 text-sm font-medium leading-tight">{c.name}</p>
                          {c.count != null && <p className="text-xs text-muted-foreground">{c.count} {c.key === "packages" ? "package" : "service"}{c.count > 1 ? "s" : ""}</p>}
                        </button>
                      ))}
                    </div>
                    {!landingCards(view.gender).length && <p className="text-sm text-muted-foreground py-10 text-center">No services available yet.</p>}
                  </>
                )}
              </div>
            )}

            {/* Service list */}
            {view.type === "svclist" && (
              <div data-testid="view-svclist">
                {renderSubHeader(view.title || "Services")}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pb-32">
                  {listServices().map((s) => <ServiceCard key={s.id} s={s} inCart={!!cart.find((x) => x.id === s.id)} onToggle={toggleService} qty={cart.find((x) => x.id === s.id)?.qty || 0} onQty={setQty} />)}
                  {!listServices().length && <p className="text-sm text-muted-foreground py-10 text-center" data-testid="svclist-empty">No services found here.</p>}
                </div>
              </div>
            )}

            {/* Packages (Women includes Freedom) */}
            {view.type === "packages" && (
              <div data-testid="view-packages">
                {renderSubHeader(view.title)}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(view.gender === "women" || view.gender === "all") && fpCfg.enabled && (fpCfg.slabs || []).length > 0 && (
                    <button type="button" onClick={startFreedom} data-testid="pkg-freedom-card"
                      className="group text-left rounded-2xl overflow-hidden border-2 border-primary bg-white shadow-sm flex flex-col">
                      <div className="relative aspect-[16/9]"><img src={HERO["women-packages"]} alt="Freedom Package" className="h-full w-full object-cover" /><span className="absolute top-2 left-2 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5 flex items-center gap-1"><Sparkles size={11} />FREEDOM</span></div>
                      <div className="p-4 flex-1 flex flex-col">
                        <h3 className="font-display text-xl">Freedom Package</h3>
                        <p className="text-xs text-muted-foreground mt-1">Pick your favourites — one fixed price.</p>
                        <div className="mt-2 space-y-0.5" data-testid="pkg-freedom-slabs">{[...fpCfg.slabs].sort((a, b) => a.count - b.count).map((s) => <p key={s.count} className="text-sm" data-testid={`pkg-freedom-slab-${s.count}`}>Any {s.count} services — <span className="text-primary font-semibold">{rupee(s.price)}</span></p>)}</div>
                        <div className="flex-1" />
                        <span className="mt-3 inline-block rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold text-center">Build my package →</span>
                      </div>
                    </button>
                  )}
                  {pkgs.filter((p) => { const g = p.gender || "women"; return view.gender === "all" ? true : view.gender === "men" ? (g === "men" || g === "unisex") : (g === "women" || g === "unisex" || !p.gender); }).map((p) => (
                    <Card key={p.id} data-testid={`home-pkg-${p.id}`} className="overflow-hidden border-border bg-white shadow-sm flex flex-col">
                      {p.image ? <img src={photoUrl(p.image, 800)} alt={p.name} loading="lazy" decoding="async" className="h-40 w-full object-cover" /> : <div className="h-40 w-full bg-secondary grid place-items-center text-primary"><Package size={40} /></div>}
                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="font-display text-xl leading-tight">{p.name}</h3>
                        {p.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
                        {(p.services || []).length > 0 && <p className="text-xs text-primary mt-2" data-testid={`home-pkg-combo-${p.id}`}>{p.services.map((s) => s.name).join("  +  ")}</p>}
                        <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                          {p.actual_price > p.offer_price && <span className="text-sm text-muted-foreground line-through">{rupee(p.actual_price)}</span>}
                          <span className="text-primary text-2xl font-semibold">{rupee(p.offer_price)}</span>
                          {p.savings > 0 && <span className="text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid={`home-pkg-save-${p.id}`}>Save {rupee(p.savings)}</span>}
                        </div>
                        {p.duration > 0 && <p className="text-xs text-muted-foreground mt-1">{p.duration} min</p>}
                        <div className="flex-1" />
                        <Button onClick={() => startPackage(p)} data-testid={`home-pkg-book-${p.id}`} className="mt-4 w-full rounded-full bg-primary text-primary-foreground hover:bg-[#9d1826] font-medium">Book Package</Button>
                      </div>
                    </Card>
                  ))}
                  {view.gender === "men" && !pkgs.length && <p className="text-sm text-muted-foreground py-10 text-center col-span-full">No men's packages available yet.</p>}
                </div>
              </div>
            )}

            <BottomBars cart={cart} gender={homeGender} onViewCart={startWizard} view={view} setView={(v) => { setView(v); setSvcQuery(""); }} onHome={goHome} showNav />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-8 mt-4 flex-wrap">
              {flow.map((k, i) => (
                <div key={k} className={`flex items-center gap-2 text-xs ${step === i ? "text-primary" : "text-muted-foreground"}`}>
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center border ${step > i ? "bg-primary text-primary-foreground border-primary" : step === i ? "border-primary" : "border-border"}`}>{step > i ? <Check size={12} /> : i + 1}</span>
                  {LABELS[k]}{i < flow.length - 1 && <span className="w-4 h-px bg-border" />}
                </div>
              ))}
            </div>

            <Button variant="ghost" size="sm" onClick={back} className="mb-4 text-muted-foreground" data-testid="book-back"><ChevronLeft size={16} /> Back</Button>
            {!["date", "time"].includes(stepKey) && <BranchBar />}

            {stepKey === "cart" && (
              <div data-testid="step-cart">
                <h1 className="font-display text-3xl mb-1">Your cart</h1>
                <p className="text-sm text-muted-foreground mb-4">{sel.branch?.name}</p>
                <div className="space-y-3">
                  {cart.map((svc) => (
                    <Card key={svc.id} className="p-4 bg-card border-border" data-testid={`cart-svc-${svc.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0"><p className="font-medium truncate">{svc.name}</p><p className="text-xs text-muted-foreground">{svc.duration || 30} min · {rupee(svc.price)} each</p></div>
                        <button type="button" onClick={() => removeSvc(svc)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Remove" data-testid={`cart-remove-${svc.id}`}><X size={16} /></button>
                      </div>
                      {items.length > 1 && Array.from({ length: svc.qty || 1 }, (_, i) => i + 1).map((n) => { if (isFirstUnit(svc, n)) return (
                        <div key={n} className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid={`cart-person-${svc.id}-u${n}`}>
                          <span className="text-muted-foreground">{(svc.qty || 1) > 1 ? `#${n} — ` : ""}This service is</span>
                          <span className="rounded-full px-3 py-1.5 border-2 font-semibold bg-primary text-white border-primary" data-testid={`cart-person-${svc.id}-u${n}-locked`}>✓ For Me</span>
                        </div>);
                        const pn = unitPerson(svc, n); const needG = pn !== 1 && !persons.find((p) => p.n === pn)?.gender; return (
                        <div key={n} className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid={`cart-person-${svc.id}-u${n}`}>
                          <span className="text-muted-foreground w-full sm:w-auto font-medium text-[#333]">{(svc.qty || 1) > 1 ? `#${n} — who is this service for?` : "Who is this service for?"}</span>
                          {persons.map((p) => <button key={p.n} type="button" onClick={() => setUnitPerson(svc, n, p.n)} aria-pressed={pn === p.n} data-testid={`cart-person-${svc.id}-u${n}-${p.n}`} className={`rounded-full px-3 py-1.5 border-2 font-semibold transition-colors ${pn === p.n ? "bg-primary text-white border-primary ring-2 ring-primary/30 shadow-sm" : "bg-white text-[#555] border-gray-300 hover:border-primary/60"}`}>{p.n === 1 ? (pn === p.n ? "✓ For Me" : "For Me") : (pn === p.n ? `✓ Person ${p.n}` : `Person ${p.n}`)}</button>)}
                          <button type="button" onClick={() => setUnitPerson(svc, n, addPerson())} data-testid={`cart-add-person-${svc.id}-u${n}`} className="rounded-full px-3 py-1.5 border-2 border-dashed border-blue-600 text-blue-700 font-semibold bg-blue-50 hover:bg-blue-100 transition-colors">{persons.length > 1 ? "+ Add Another Person" : "+ Another Person"}</button>
                          {(pn !== 1) && (
                            <span className={`w-full sm:w-auto sm:ml-auto flex items-center gap-1 ${needG ? "animate-pulse" : ""}`} data-testid={`cart-gender-${svc.id}-u${n}`}>
                              <span className="text-muted-foreground mr-1">{needG ? "Select gender:" : ""}</span>
                              {[["male", "Male"], ["female", "Female"]].map(([g, l]) => <button key={g} type="button" onClick={() => setPersonGender(pn, g)} data-testid={`cart-gender-${svc.id}-u${n}-${g}`} className={`rounded-full px-3 py-1.5 border-2 font-semibold transition-colors ${personGender(pn, svc) === g ? "bg-[#1c1c1c] text-white border-[#1c1c1c] ring-2 ring-black/20 shadow-sm" : `bg-white text-[#555] ${needG ? "border-blue-500" : "border-gray-300"} hover:border-black/60`}`}>{personGender(pn, svc) === g ? "✓ " : ""}{l}</button>)}
                            </span>
                          )}
                        </div>); })}
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-2" data-testid={`cart-qty-${svc.id}`}>
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-full" onClick={() => adjustQty(svc, (svc.qty || 1) - 1)} data-testid={`cart-minus-${svc.id}`}>−</Button>
                          <span className="w-5 text-center font-semibold" data-testid={`cart-count-${svc.id}`}>{svc.qty || 1}</span>
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-full" disabled={(svc.qty || 1) >= 4} onClick={() => adjustQty(svc, (svc.qty || 1) + 1)} data-testid={`cart-plus-${svc.id}`}>+</Button>
                        </div>
                        <span className="font-semibold" data-testid={`cart-subtotal-${svc.id}`}>{rupee((svc.price || 0) * (svc.qty || 1))}</span>
                      </div>
                    </Card>
                  ))}
                </div>
                <Card className="p-4 bg-card border-border mt-4 text-sm space-y-1" data-testid="cart-totals">
                  <div className="flex justify-between"><span className="text-muted-foreground">{items.length} service unit{items.length > 1 ? "s" : ""} · {fmtDur(totalDuration)}</span><span className="font-semibold text-primary" data-testid="cart-total">{rupee(totalPrice)}</span></div>
                </Card>
                <div className="flex gap-2 mt-5">
                  <Button variant="outline" onClick={() => { setActive(false); }} className="rounded-sm h-12 flex-1" data-testid="cart-add-more">+ Add more services</Button>
                  <Button onClick={() => { const missing = items.find((it) => it.person !== 1 && !persons.find((p) => p.n === it.person)?.gender); if (missing) { toast.error(`Select Male/Female for Person ${missing.person}`); return; } setStep(step + 1); }} disabled={!cart.length} className="rounded-sm h-12 flex-1 bg-primary text-primary-foreground hover:bg-[#9d1826] font-semibold" data-testid="cart-continue">Book Now</Button>
                </div>
              </div>
            )}

            {stepKey === "details" && (
              <div className="max-w-md" data-testid="step-details">
                <h1 className="font-display text-3xl mb-1">Your details</h1>
                <p className="text-sm text-muted-foreground mb-4">{cart.length} service{cart.length > 1 ? "s" : ""} · {fmtDur(totalDuration)} · {rupee(totalPrice)}</p>
                <div className="space-y-3">
                  <Input placeholder="Mobile number" value={cust.mobile} inputMode="numeric" maxLength={10} autoComplete="tel"
                    onChange={(e) => setCust({ ...cust, id: null, mobile: e.target.value.replace(/\D/g, "") })} className="bg-secondary" data-testid="book-mobile" />
                  {prefilled && <p className="text-xs text-primary flex items-center gap-1" data-testid="book-welcome-back"><Check size={12} /> Welcome back, {cust.name}!</p>}
                  <Input placeholder="Your name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} className="bg-secondary" data-testid="book-name" />
                  <Button onClick={() => {
                    if (!cust.name.trim()) { toast.error("Please enter your name"); return; }
                    if ((cust.mobile || "").replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
                    setStep(step + 1);
                  }} data-testid="book-details-continue"
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-[#9d1826] disabled:opacity-50 h-12 font-semibold tracking-wide">Continue</Button>
                </div>
              </div>
            )}

            {stepKey === "branch" && (
              <div className="grid gap-3" data-testid="step-branch">
                <h1 className="font-display text-3xl mb-2">Select a branch</h1>
                {branches.map((b) => <BranchCard key={b.id} b={b} on={sel.branch?.id === b.id} testId={`book-branch-${b.id}`} onPick={() => { setSel({ ...sel, branch: b, time: null }); if (method === "combopkg") loadStaffEligible(b.id); setStep(step + 1); }} />)}
              </div>
            )}

            {stepKey === "method" && (
              <div data-testid="step-method">
                <h1 className="font-display text-3xl mb-1">How would you like to book?</h1>
                <p className="text-sm text-muted-foreground mb-6">{sel.branch?.name} · {cart.length} service{cart.length > 1 ? "s" : ""}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Card onClick={() => { setMethod("staff"); setStep(step + 1); setStaffPick(null); setSel((s) => ({ ...s, time: null })); loadStaffEligible(); }}
                    data-testid="method-staff" className="p-8 bg-card border-border hover:border-primary cursor-pointer transition-colors text-center">
                    <div className="h-14 w-14 rounded-full bg-primary/15 text-primary flex items-center justify-center mx-auto mb-4"><User size={26} /></div>
                    <p className="font-display text-2xl">Book by Staff</p>
                    <p className="text-sm text-muted-foreground mt-2">Choose your stylist first, then a date & their available time.</p>
                  </Card>
                  <Card onClick={() => { setMethod("time"); setStep(step + 1); setStaffPick(null); setSel((s) => ({ ...s, time: null })); }}
                    data-testid="method-time" className="p-8 bg-card border-border hover:border-primary cursor-pointer transition-colors text-center">
                    <div className="h-14 w-14 rounded-full bg-primary/15 text-primary flex items-center justify-center mx-auto mb-4"><Clock size={26} /></div>
                    <p className="font-display text-2xl">Book by Time</p>
                    <p className="text-sm text-muted-foreground mt-2">Pick a date & time first, then choose from available stylists.</p>
                  </Card>
                </div>
              </div>
            )}

            {stepKey === "fpservices" && (
              <div data-testid="step-fpservices">
                <h1 className="font-display text-3xl mb-1">Women's Freedom Package</h1>
                <p className="text-sm text-muted-foreground mb-4">{sel.branch?.name} · Pick any {fpMin}–{fpMax} services — your price is set automatically.</p>
                <div className="flex flex-wrap gap-2 mb-5" data-testid="fp-tiers">
                  {[...fpCfg.slabs].sort((a, b) => a.count - b.count).map((s) => (
                    <div key={s.count} data-testid={`fp-tier-${s.count}`}
                      className={`rounded-full px-4 py-2 text-sm border ${cart.length === s.count ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>Any {s.count} Services · {rupee(s.price)}</div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-2" data-testid="fp-pick-hint">Select {fpMin}–{fpMax} services ({cart.length} selected)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pb-24">
                  {fpCfg.services.map((s) => {
                    const inCart = !!cart.find((x) => x.id === s.id);
                    const atLimit = cart.length >= fpMax && !inCart;
                    return (
                      <Card key={s.id} data-testid={`fp-service-${s.id}`} className={`p-4 border flex flex-col ${inCart ? "bg-primary/10 border-primary" : atLimit ? "bg-card border-border opacity-50" : "bg-card border-border hover:border-primary"}`}>
                        <div className="flex-1"><h3 className="font-display text-lg leading-tight">{s.name}</h3><p className="text-xs text-primary/70 mt-1">{s.category} · {s.duration} min</p></div>
                        <Button onClick={() => toggleFpService(s)} disabled={atLimit} variant={inCart ? "default" : "outline"} data-testid={`fp-select-${s.id}`}
                          className={`mt-3 w-full rounded-full ${inCart ? "bg-primary text-primary-foreground hover:bg-[#9d1826]" : "border-primary/40 text-primary hover:bg-primary/10"}`}>{inCart ? <><Check size={16} className="mr-1" />Selected</> : "Select"}</Button>
                      </Card>
                    );
                  })}
                </div>
                <div className="fixed inset-x-0 bottom-0 z-30 border-t border-primary/30 bg-background/95 backdrop-blur-xl">
                  <div className="max-w-6xl mx-auto p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm" data-testid="fp-cart-summary">{cart.length} service{cart.length === 1 ? "" : "s"} selected</p>
                      <p className="text-xs text-muted-foreground" data-testid="fp-package-total">{fpMode ? <>Package Total <span className="text-primary font-semibold">{rupee(fpMode.price)}</span></> : (cart.length < fpMin ? `Select at least ${fpMin} services` : "—")}</p>
                    </div>
                    <Button onClick={() => setStep(step + 1)} disabled={!fpMode} data-testid="fp-continue" className="rounded-full h-12 px-8 bg-primary text-primary-foreground hover:bg-[#9d1826] font-semibold disabled:opacity-50">Continue</Button>
                  </div>
                </div>
              </div>
            )}

            {stepKey === "fpstaff" && (
              <div data-testid="step-fpstaff">
                <h1 className="font-display text-3xl mb-1">Choose staff for each service</h1>
                <p className="text-sm text-muted-foreground mb-4">{prettyDate(sel.date)} · {sel.branch?.name}</p>
                {loading && <p className="text-muted-foreground" data-testid="fpstaff-loading">Loading stylists…</p>}
                <div className="space-y-3">
                  {items.map((it, idx, all) => { const svc = it.svc; const k = method === "freedom" ? svc.id : it.key; const showN = (svc.qty || 1) > 1 || method === "multi";
                    const firstOfPerson = method === "multi" && all.findIndex((x) => x.person === it.person) === idx; const units = personUnits(it.person); const common = firstOfPerson && units.length > 1 ? commonStaffFor(it.person) : [];
                    const allSame = units.length > 1 && units.every((u) => fpStaff[u.key] && fpStaff[u.key] === fpStaff[units[0].key]); return (
                    <div key={it.key} className="space-y-3">
                    {firstOfPerson && units.length > 1 && (
                      <Card className="p-3 bg-primary/5 border-primary/40" data-testid={`fp-person-common-${it.person}`}>
                        <p className="text-sm font-medium">{it.person === 1 ? "For Me" : `Person ${it.person}`}{persons.length > 1 && personGender(it.person, svc) ? ` · ${personGender(it.person, svc) === "male" ? "Male" : "Female"}` : ""} — {units.length} services, back-to-back</p>
                        {common.length ? (<>
                          <p className="text-xs text-muted-foreground mt-0.5">One stylist for all {units.length} services{allSame ? " ✓" : ""}:</p>
                          <div className="flex flex-wrap gap-2 mt-2">{common.map((st) => <button key={st.id} type="button" onClick={() => assignPersonStaff(it.person, st.id)} data-testid={`fp-person-common-${it.person}-${st.id}`} className={`rounded-full px-3 py-1.5 border-2 text-sm font-medium ${allSame && fpStaff[units[0].key] === st.id ? "bg-primary text-white border-primary ring-2 ring-primary/30" : "bg-white border-border text-[#333]"}`}>{allSame && fpStaff[units[0].key] === st.id ? "✓ " : ""}{st.name}</button>)}</div>
                        </>) : <p className="text-xs text-muted-foreground mt-0.5">No single stylist can do all these services — pick per service below.</p>}
                      </Card>
                    )}
                    <Card className="p-4 bg-card border-border" data-testid={`fp-staff-row-${it.key.replace("#", "-")}`}>
                      <div className="flex items-center justify-between mb-2"><span className="font-medium">{svc.name}{showN ? ` #${it.n}` : ""}{method === "multi" && persons.length > 1 ? <span className="ml-2 text-xs text-muted-foreground">{it.person === 1 ? "For Me" : `Person ${it.person}`}{personGender(it.person, svc) ? ` · ${personGender(it.person, svc) === "male" ? "Male" : "Female"}` : ""}</span> : null}</span><span className="text-xs text-muted-foreground">{svc.duration} min · {rupee(method === "freedom" ? svc.actual_price : svc.price)}</span></div>
                      <div className="flex flex-wrap gap-2">
                        {(fpEligible[svc.id] || []).map((st) => (
                          <button key={st.id} type="button" onClick={() => setFpStaff((m) => ({ ...m, [k]: st.id }))} data-testid={`fp-staff-${it.key.replace("#", "-")}-${st.id}`}
                            className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border-2 text-sm font-medium transition-colors ${fpStaff[k] === st.id ? "bg-primary text-white border-primary ring-2 ring-primary/30" : "bg-white border-border text-[#333]"}`}>
                            {st.photo ? <img src={photoUrl(st.photo)} alt="" className="h-7 w-7 rounded-full object-cover" /> : <span className="h-7 w-7 rounded-full bg-primary/20 text-primary grid place-items-center text-xs">{st.name[0]}</span>}{st.name}</button>
                        ))}
                        {!loading && !(fpEligible[svc.id] || []).length && <span className="text-sm text-muted-foreground">No stylist available for this service on {prettyDate(sel.date)}.</span>}
                      </div>
                    </Card>
                    </div>
                  ); })}
                </div>
                <Button onClick={() => { loadFreedomSlots(); setStep(step + 1); }} disabled={items.some((it) => !itemStaff(it))} data-testid="fp-staff-continue"
                  className="w-full mt-5 rounded-sm bg-primary text-primary-foreground hover:bg-[#9d1826] disabled:opacity-50 h-12 font-semibold">Continue</Button>
              </div>
            )}

            {stepKey === "date" && (
              <div data-testid="step-date">
                <h1 className="font-display text-3xl mb-4">Pick a date</h1>
                {method === "staff" && staffPick && <p className="text-sm text-muted-foreground mb-3">Stylist: <span className="text-primary">{staffPick.name}</span></p>}
                <div className="flex flex-wrap gap-2 mb-4" data-testid="book-date-quick">
                  {[0, 1, 2].map((off) => { const d = istNow(); d.setDate(d.getDate() + off); const v = ymd(d); const on = sel.date === v; return (
                    <button key={off} type="button" onClick={() => setSel({ ...sel, date: v, time: null })} aria-pressed={on} data-testid={`book-date-quick-${off}`}
                      className={`rounded-full px-4 py-2 text-sm border-2 font-semibold transition-colors ${on ? "bg-primary text-white border-primary ring-2 ring-primary/30 shadow-sm" : "bg-white text-[#333] border-gray-300 hover:border-primary/60"}`}>
                      {on ? "✓ " : ""}{off === 0 ? "Today" : off === 1 ? "Tomorrow" : "After Tomorrow"}<span className={`block text-[11px] font-normal ${on ? "text-white/80" : "text-muted-foreground"}`}>{d.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                    </button>); })}
                </div>
                <div className="mb-4 w-full max-w-[280px]">
                  <label htmlFor="book-date" className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                    <CalendarIcon size={14} className="text-primary" /> Or pick another date
                  </label>
                  <Input id="book-date" type="date" data-testid="book-date-input"
                    value={sel.date || ""} min={today()}
                    onChange={(e) => { if (e.target.value) setSel({ ...sel, date: e.target.value, time: null }); }}
                    className={`rounded-sm ${sel.date ? "bg-primary/10 border-primary" : "bg-secondary"}`} />
                  {sel.date ? <p className="text-sm text-primary mt-1.5 font-semibold" data-testid="book-date-pretty">✓ {prettyDate(sel.date)}</p>
                    : <p className="text-sm text-primary mt-1.5" data-testid="book-date-required">Please select a date to continue.</p>}
                </div>
                <p className="text-sm text-muted-foreground mb-2">{cart.length} service{cart.length > 1 ? "s" : ""} · Total duration: {fmtDur(totalDuration)}</p>
                <div className="mt-6"><BranchBar /></div>
                <div className="sticky bottom-4 bg-card/90 backdrop-blur border border-border rounded-md p-3 flex items-center justify-between gap-3" data-testid="book-date-bar">
                  <span className="text-sm text-muted-foreground">{sel.date ? prettyDate(sel.date) : "Please select a date to continue."} · {fmtDur(totalDuration)}</span>
                  <Button onClick={() => { if (method === "freedom" || method === "multi") { loadFpEligible(); } else if (method === "staff" || method === "combopkg") { loadStaffSlots(); } else { loadComboSlots(); } setStep(step + 1); }}
                    disabled={!sel.date} data-testid="book-date-continue"
                    className="rounded-sm bg-primary text-primary-foreground hover:bg-[#9d1826] disabled:opacity-50">{method === "freedom" || method === "multi" ? "Continue to Staff" : "Continue to Time"}</Button>
                </div>
              </div>
            )}

            {stepKey === "time" && (
              <div data-testid="step-time">
                <h1 className="font-display text-3xl mb-2">Pick a start time</h1>
                <p className="text-sm text-muted-foreground mb-4">{prettyDate(sel.date)} · {sel.branch?.name}{method === "staff" && staffPick ? ` · ${staffPick.name}` : ""} · {fmtDur(totalDuration)}</p>
                <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-secondary border border-border inline-block" /> Available</span>
                  <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-destructive/20 border border-destructive/40 inline-block" /> Booked</span>
                </div>
                {loading && <p className="text-muted-foreground" data-testid="slots-loading">Checking availability…</p>}
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {slots.map((s) => (
                    <Button key={s.time} variant="ghost" data-testid={`book-slot-${s.time}`}
                      disabled={s.available === false}
                      title={s.available === false ? "Booked / unavailable" : "Available"}
                      onClick={() => setSel({ ...sel, time: s.time })}
                      className={`rounded-sm h-auto min-h-9 py-1 relative flex flex-col leading-tight transition-colors duration-150 ${s.available === false
                        ? "bg-destructive/10 text-destructive/70 cursor-not-allowed opacity-70 hover:bg-destructive/10"
                        : sel.time === s.time
                          ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-1 ring-offset-background"
                          : "bg-secondary hover:bg-primary hover:text-primary-foreground"}`}>
                      <span className={s.available === false ? "line-through" : ""}>{to12h(s.time)}</span>
                      {s.available === false && <span className="text-[9px] font-semibold tracking-wide">BOOKED</span>}
                    </Button>
                  ))}
                  {!loading && offMsg && <p className="col-span-full text-sm text-destructive py-4" data-testid="book-staff-off">{offMsg}</p>}
                  {!loading && !offMsg && !slots.some((s) => s.available) && <p className="col-span-full text-sm text-muted-foreground py-4" data-testid="book-no-slots">No time slots available for this selection. Please try another {method === "staff" ? "date or stylist" : "date"}.</p>}
                </div>
                <div className="mt-5"><BranchBar /></div>
                <Button onClick={() => { if (method === "time") loadComboStaff(); setStep(step + 1); }} disabled={!sel.time} data-testid="book-time-continue"
                  className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-[#9d1826] disabled:opacity-50 h-12 font-semibold tracking-wide">Select Slot & Continue</Button>
              </div>
            )}

            {stepKey === "staff" && (
              <div data-testid="step-staff">
                <h1 className="font-display text-3xl mb-1">{method === "staff" ? "Choose your stylist" : "Available stylists"}</h1>
                <p className="text-sm text-muted-foreground mb-4">{sel.branch?.name}{method === "time" ? ` · ${prettyDate(sel.date)} · ${to12h(sel.time)}` : ""} · {cart.length} service{cart.length > 1 ? "s" : ""} · {fmtDur(totalDuration)}</p>
                {loading && <p className="text-muted-foreground" data-testid="staff-loading">Loading stylists…</p>}
                {!loading && !staffOptions.length && (
                  <Card className="p-6 bg-card border-border text-center" data-testid="book-no-staff">
                    <p className="text-muted-foreground text-sm">{method === "staff"
                      ? "No stylist at this branch can perform all the selected services. Try removing a service."
                      : "No stylist is free for the full duration at this time. Please pick another time."}</p>
                    <Button variant="outline" className="mt-3 rounded-sm" onClick={back} data-testid="book-staff-goback">Go Back</Button>
                  </Card>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  {staffOptions.map((s) => (
                    <Card key={s.id} onClick={() => { setStaffPick(s); setStep(step + 1); }} data-testid={`book-staff-${s.id}`}
                      className={`p-4 border cursor-pointer transition-colors duration-150 flex items-center gap-3 ${staffPick?.id === s.id ? "bg-primary/10 border-primary" : "bg-card border-border hover:border-primary"}`}>
                      {s.photo ? <img src={photoUrl(s.photo)} alt={s.name} className="h-14 w-14 rounded-full object-cover" /> : <div className="h-14 w-14 rounded-full bg-primary/20 text-primary flex items-center justify-center font-display text-xl">{s.name[0]}</div>}
                      <div>
                        <p className="font-medium">{s.name}</p>
                        {s.designation && <p className="text-xs text-primary">{s.designation}</p>}
                        <p className="flex items-center gap-1 text-xs mt-0.5"><Star size={11} fill="#C5A059" className="text-primary" /> {s.rating || 0} <span className="text-muted-foreground">({s.review_count || 0})</span></p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {stepKey === "summary" && (
              <div className="max-w-lg" data-testid="step-summary">
                <h1 className="font-display text-3xl mb-4">Appointment summary</h1>
                {method === "freedom" && fpMode && (
                  <div className="mb-5 rounded-xl border border-green-500/40 bg-green-500/10 px-5 py-4 text-center" data-testid="fp-savings-banner">
                    <p className="font-display text-3xl text-green-300">You save {rupee(cart.reduce((a, x) => a + (Number(x.actual_price) || 0), 0) - fpMode.price)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Actual value {rupee(cart.reduce((a, x) => a + (Number(x.actual_price) || 0), 0))} · You pay only {rupee(fpMode.price)}</p>
                  </div>
                )}
                {method === "combopkg" && selectedPkg && (
                  <div className="mb-5 rounded-xl border border-green-500/40 bg-green-500/10 px-5 py-4 text-center" data-testid="pkg-savings-banner">
                    <p className="font-display text-2xl text-primary">{selectedPkg.name}</p>
                    <p className="font-display text-3xl text-green-300 mt-1">You save {rupee(selectedPkg.savings || 0)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Actual value {rupee(selectedPkg.actual_price || 0)} · You pay only {rupee(selectedPkg.offer_price || 0)}</p>
                  </div>
                )}
                <Card className="p-5 bg-secondary border-border text-sm space-y-2" data-testid="book-summary-card">
                  <Row label="Customer" value={cust.name} />
                  <Row label="Mobile" value={cust.mobile} />
                  {method === "freedom" && <Row label="Package" value={`Women's Freedom Package · ${cart.length} services`} />}
                  {method === "combopkg" && <Row label="Package" value={selectedPkg?.name} />}
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Service(s)</p>
                    {blockTimes().map((bt, i, all) => { const perUnit = method === "freedom" || method === "multi"; const cur = itemStaff(bt.it); const open = unitEdit === bt.it.key; const firstOfPerson = method === "multi" && persons.length > 1 && all.findIndex((x) => x.it.person === bt.it.person) === i; return (
                      <div key={i} data-testid={`summary-svc-${i}`}>
                        {firstOfPerson && <p className="text-xs font-semibold text-primary mt-2 mb-1" data-testid={`summary-person-${bt.it.person}`}>{bt.it.person === 1 ? "For Me" : `Person ${bt.it.person}`}{personGender(bt.it.person, bt.it.svc) ? ` – ${personGender(bt.it.person, bt.it.svc) === "male" ? "Male" : "Female"}` : ""}</p>}
                        <button type="button" disabled={!perUnit} onClick={() => setUnitEdit(open ? null : bt.it.key)} className={`w-full flex justify-between text-left ${perUnit ? "hover:text-primary" : ""}`} data-testid={`summary-unit-${bt.it.key.replace("#", "-")}`}>
                          <span>{to12h(bt.start)}–{to12h(bt.end)} · {bt.it.svc.name}{(bt.it.svc.qty || 1) > 1 ? ` #${bt.it.n}` : ""}{perUnit ? <> · <span className="underline decoration-dotted">{(fpEligible[bt.it.svc.id] || []).find((x) => x.id === cur)?.name || "Select stylist"}</span></> : ""}</span>
                          <span className="text-primary">{method === "freedom" ? rupee(bt.it.svc.actual_price) : rupee(bt.it.svc.price)}</span>
                        </button>
                        {open && (
                          <div className="flex flex-wrap gap-2 my-2" data-testid={`summary-unit-staff-${bt.it.key.replace("#", "-")}`}>
                            {(fpEligible[bt.it.svc.id] || []).map((st) => (
                              <button key={st.id} type="button" onClick={() => changeUnitStaff(bt.it, st.id)} data-testid={`summary-pick-${bt.it.key.replace("#", "-")}-${st.id}`}
                                className={`rounded-full px-3 py-1 border text-xs ${cur === st.id ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>{st.name}</button>
                            ))}
                          </div>
                        )}
                      </div>); })}
                    {(method === "freedom" || method === "multi") && <p className="text-[11px] text-muted-foreground mt-1">Tap a service line to change its stylist.</p>}
                  </div>
                  {method === "freedom" ? (
                    <>
                      <Row label="Actual Service Value" value={rupee(cart.reduce((a, x) => a + (Number(x.actual_price) || 0), 0))} />
                      <Row label="Freedom Package Payable" value={rupee(fpMode?.price || 0)} />
                    </>
                  ) : method === "combopkg" ? (
                    <>
                      <Row label="Actual Value" value={rupee(selectedPkg?.actual_price || 0)} />
                      <Row label="Package Price" value={rupee(selectedPkg?.offer_price || 0)} />
                    </>
                  ) : (
                    <Row label="Total Price" value={rupee(totalPrice)} />
                  )}
                  <Row label="Total Duration" value={fmtDur(totalDuration)} />
                  <Row label="Branch" value={sel.branch?.name} />
                  <Row label="Date" value={prettyDate(sel.date)} />
                  <Row label="Start Time" value={to12h(sel.time)} />
                  {method !== "freedom" && method !== "multi" && <Row label="Stylist" value={staffPick?.name} />}
                  <Row label="Expected Finish" value={to12h(finishTime)} />
                </Card>
                <Button onClick={book} disabled={booking} data-testid="book-now"
                  className="w-full mt-5 rounded-sm bg-primary text-primary-foreground hover:bg-[#9d1826] disabled:opacity-60 h-12 font-semibold tracking-wide">{booking ? "Booking…" : "BOOK NOW"}</Button>
              </div>
            )}
          </>
        )}
      </div>
      <BookingChat />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-right font-medium">{value || "—"}</span>
    </div>
  );
}

function BookingChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "Hi! 👋 Tell me what you'd like — e.g. \"3 PM par facial available hai?\" — and I'll book it for you." }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sid = useRef("web-" + Math.random().toString(36).slice(2, 10));
  const boxRef = useRef(null);
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput(""); setMsgs((m) => [...m, { role: "user", content: text }]); setSending(true);
    try {
      const { data } = await api.post("/ai/booking", { message: text, session_id: sid.current });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, please try again in a moment." }]);
    } finally { setSending(false); }
  };

  return (
    <>
    </>
  );
}
